/**
 * The pieces every answer panel shares.
 *
 * Each answer shape gets its own file, but they all owe the reader the same three things: a
 * HEADLINE that states the answer in words, the FIGURES it is built from, and the CAVEAT that
 * says what the figures do not cover. The caveat is not decoration — a place roll-up that
 * overlaps, a trail bounded by the query window and a compliance ratio over a sample are each a
 * number that means something narrower than it looks.
 */
import type { ReactNode } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { CameraRef, Group, SearchPlaceBy } from '../../../api/contracts';

/**
 * Resolves a place ID to a name.
 *
 * The API returns IDs — a camera or group UUID, or a zone string — and never names, because the
 * command-center holds neither the camera list nor the group tree. The page already loads both
 * for its filter controls, so this closes over those rather than adding a second source of names
 * that could disagree with the chips.
 */
export interface PlaceNamer {
  (place: string, placeBy: SearchPlaceBy): string;
  }

export function makePlaceNamer(cameras: CameraRef[], groups: Group[]): PlaceNamer {
  const cameraNames = new Map(cameras.map((c) => [c.id, c.name]));
  const groupNames = new Map((groups as any[]).map((g) => [g.id, g.name as string]));

  return (place: string, placeBy: SearchPlaceBy) => {
    if (placeBy === 'zone') return place;
    const name = placeBy === 'group' ? groupNames.get(place) : cameraNames.get(place);
    // A truncated UUID rather than the whole thing: it is not a name, and it should not look like
    // one, but it still lets someone match the row against a URL or a log line.
    return name || place.slice(0, 8);
  };
}

/** The parent of a group, for rendering the roll-up as the tree it is. */
export function makeParentOf(groups: Group[]): (id: string) => string | null {
  const parents = new Map((groups as any[]).map((g) => [g.id as string, (g.parent_id as string) || null]));
  return (id: string) => parents.get(id) ?? null;
}

export const fmtTime = (iso: string | null | undefined) => (iso
  ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  : '—');

export const fmtDateTime = (iso: string | null | undefined) => (iso
  ? new Date(iso).toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  : '—');

/**
 * A duration in the largest unit that still carries information.
 *
 * "0s" is a real answer and is shown as such: a stop with one sighting proves presence, not
 * duration, and rounding it up to "a minute" would invent a span nobody measured.
 */
export function fmtDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

/** One figure, stated in words next to its number. */
export function Figure({ value, label, tone = 'default' }: {
  value: ReactNode;
  label: string;
  tone?: 'default' | 'good' | 'bad';
}) {
  const colour = tone === 'good' ? 'text-emerald-500' : tone === 'bad' ? 'text-red-500' : 'text-foreground';
  return (
    <div>
      <div className={`text-2xl font-semibold leading-none tabular-nums ${colour}`}>{value}</div>
      <div className="mt-1 text-[11px] leading-tight text-muted-foreground">{label}</div>
    </div>
  );
}

/**
 * The caveat line. Rendered as information rather than a warning: the answer is correct, and this
 * says what it is correct ABOUT.
 */
export function Caveat({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
      <InformationCircleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/** The frame every answer sits in: headline, figures, body, caveats. */
export function AnswerShell({ headline, sub, figures, caveats, children }: {
  headline: ReactNode;
  sub?: ReactNode;
  figures?: ReactNode;
  caveats?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">{headline}</h2>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      </div>

      {figures && <div className="flex flex-wrap items-start gap-x-8 gap-y-3">{figures}</div>}

      {children}

      {caveats && <div className="mt-auto space-y-1 border-t border-border pt-2">{caveats}</div>}
    </div>
  );
}

/** Nothing matched. Says which of the two reasons it was, because the fixes differ. */
export function AnswerEmpty({ cameraScope, hint }: { cameraScope?: number; hint?: string }) {
  return (
    <div className="p-8 text-center">
      <p className="text-sm font-medium text-foreground">
        {cameraScope === 0 ? 'No cameras in scope' : 'Nothing matched'}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {cameraScope === 0
          ? 'You do not have access to any camera matching these filters.'
          : hint || 'Try widening the time window, or removing an appearance filter.'}
      </p>
    </div>
  );
}
